<template>
  <div
    class="messy-high-term container"
    v-loading.fullscreen.lock="fullLoading"
  >
    <el-tooltip
      placement="top"
      content="请提交所有错误句子后再点击下一步哦！"
      :disabled="canSubmit"
    >
      <el-button
        class="next-btn"
        :class="{ disabled: loading || !canSubmit }"
        type="primary"
        @click="canSubmit && (hasNext ? next(STEP.three) : handleAllSubmit())"
      >
        {{ hasNext ? '下一步' : '提交' }}
      </el-button>
    </el-tooltip>
    <div class="operate-bar">
      <el-button
        class="batch-submit"
        type="primary"
        @click="batchSubmit"
        :disabled="!multiSelection?.length"
      >
        批量提交
      </el-button>
    </div>
    <div class="table-wrap">
      <el-table
        border
        v-loading="loading"
        :span-method="arraySpanMethod"
        :data="records"
        :row-class-name="
          ({ row }) => row.qcStatus === submitStatusEnum.done && 'submit'
        "
        @selection-change="handleSelectionChange"
      >
        <el-table-column type="selection" width="55" />
        <el-table-column prop="srcText" label="高频词原文"></el-table-column>
        <el-table-column prop="tgtText" label="高频词译文">
          <template slot-scope="scope">
            {{ scope.row.tgtText }}
            <div>
              <el-popover
                v-model="scope.row.showPopover"
                placement="bottom"
                width="230"
                trigger="click"
                popper-class="hign-term-popover"
                @hide="handlePopHide"
              >
                <div class="txt-replace-wrap">
                  <div>
                    <p>
                      将本行译文中的
                      <span class="highlight">{{ scope.row.tgtText }}</span>
                      全部替换为：
                    </p>
                    <el-input
                      size="mini"
                      type="text"
                      :maxlength="Math.ceil(scope.row.tgtText.length * 1.8)"
                      v-model="replaceText"
                    />
                    <p>替换后无法恢复，请确认后操作</p>
                    <div class="operate-btns">
                      <el-button
                        size="mini"
                        @click="handlePopCancel(scope.row)"
                      >
                        取消
                      </el-button>
                      <el-button
                        size="mini"
                        type="primary"
                        :disabled="replaceText?.trim() === ''"
                        @click="handlePopEnsure(scope.row)"
                      >
                        确认
                      </el-button>
                    </div>
                  </div>
                </div>
                <el-button slot="reference" v-if="scope.row.tgtText">
                  替换
                </el-button>
              </el-popover>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="稿件原文" class="test" width="255">
          <template slot-scope="scope" v-if="scope.row && !loading">
            <ul class="highTermList">
              <li
                :class="[
                  scope.row.highTermSentVos.length > 1
                    ? 'li-border-b d-f align-c p-lr-10 p-tb-5'
                    : 'd-f align-c p-lr-10 p-tb-5',
                ]"
                v-for="(item, index) in scope.row.highTermSentVos"
                v-if="
                  scope.row.tarLimit === 'infinite' ||
                  index < scope.row.tarLimit
                "
                :key="index"
              >
                <div class="w-254 p-r-5">
                  <rich-text :pId="item.id" :jsonText="item.srcTxt" readonly />
                </div>
                <div class="w-258">
                  <rich-text
                    :pId="item.id"
                    :key="`${item.id}-${item.updateSign}`"
                    :hasToolbar="item.useRichText"
                    :ref="'richText-' + item.id"
                    :class="{
                      'edit-un-submit': item.editedText !== item.oriText,
                    }"
                    :jsonText="item.qcTxt"
                    :errors="item.errors"
                    @change="(text, json) => handleChange(text, json, item)"
                    @focus="handleFocus"
                    @blur="(text, json) => handleBlur(text, json, item)"
                  />
                </div>
              </li>
            </ul>
            <div
              class="t-c p-tb-5"
              v-if="scope.row?.highTermSentVos.length > LIMIT"
            >
              <span class="c-p c-primary" @click="changeShowAll(scope.row)">
                {{ scope.row.showAll ? '收起' : '展示全部包含此译文的句子' }}
              </span>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="稿件译文">
          <template slot-scope="scope" v-if="scope.row && !loading"></template>
        </el-table-column>
        <el-table-column width="150" align="center" label="操作">
          <template slot-scope="scope">
            <el-button type="primary" @click="handleSubmit(scope.row)">
              {{ scope.row.qcStatus === submitStatusEnum.done ? '已提交' : '提交' }}
            </el-button>
          </template>
        </el-table-column>
      </el-table>
      <el-pagination
        background
        layout="prev, pager, next"
        :current-page.sync="page.current"
        :total="page.total"
        :page-size="page.size"
        @current-change="handleCurrentChange"
      ></el-pagination>
    </div>
  </div>
</template>

<script>
import { getMessyHighTerm, modifyHighTerm } from '@/api/qc/check';
import RichText, { jsonTextToText } from '@/components/rich-text';
import { ERROE_EMBED } from '@/components/rich-text/const';
import loadAndCheckMixin from './loadAndCheckMixin.js';
import { hasValidChar } from '@/util/string';

const LIMIT = 3;
export default {
  data() {
    return {
      LIMIT,
      replaceText: '',
      expandStatus: {},
    };
  },
  name: 'messy-high-term',
  // ⚠️：部分公共逻辑在mixin中！
  mixins: [loadAndCheckMixin],
  components: { RichText },
  computed: {
    hasNext() {
      return this.flowCount > 3;
    },
  },
  methods: {
    // 表格合并列
    arraySpanMethod({ row, column, rowIndex, columnIndex }) {
      if (columnIndex === 3) {
        return [1, 2];
      } else if (columnIndex === 4) {
        return [0, 0];
      }
    },

    changeShowAll(row) {
      row.showAll = !row.showAll;
      if (row.showAll) {
        row.tarLimit = 'infinite';
      } else {
        row.tarLimit = LIMIT;
      }
    },
    handleAllSubmit() {
      this.$confirm('确认要提交吗', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
      })
        .then(() => {
          this.next(this.STEP.three);
        })
        .catch(() => {});
    },
    handlePopHide() {
      this.replaceText = '';
    },
    handlePopCancel(row) {
      row.showPopover = false;
    },
    handlePopEnsure(row) {
      if (!row.tgtText) return;
      row.showPopover = false;
      row.highTermSentVos.forEach((item) => {
        this.$refs['richText-' + item.id][0].replaceEmbedText(
          ERROE_EMBED.custom_error,
          this.replaceText,
        );
      });
    },
    rebuildTermErrors(coords) {
      return (
        coords?.map((item) => ({
          errorType: 'custom_error',
          position: [item.spos, item.epos],
          payload: [],
        })) ?? []
      );
    },
    fetchListData() {
      this.loading = true;
      getMessyHighTerm({
        current: this.page.current,
        size: this.page.size,
        orderId: this.queryParams.id,
      }).then((res) => {
        this.loading = false;
        const usableData = res.data?.data ?? {};
        this.page.total = usableData.total;
        // 是否可以提交
        this.canSubmit = usableData.hitCount;
        this.records = usableData.records
          ?.map((item) => {
            // 自定义的字段需要额外添加，防止丢失响应式
            item.highTermSentVos = item?.highTermSentVos.map((sent) => ({
              ...sent,
              editedText: sent.qcTxt && jsonTextToText(sent.qcTxt),
              // qcTxt 是jsonStr类型，这里需要转成text类型，用于比对
              oriText: sent.qcTxt && jsonTextToText(sent.qcTxt),
              errors: this.rebuildTermErrors(sent.posVos),
              editedJson: sent.qcTxt,
              updateSign: 0,
            }));
            item.showPopover = false;
            return item;
          })
          .map((item) => {
            return { ...item, showAll: false, tarLimit: LIMIT };
          });
        this.recoverExpandStatus();
      });
    },
    handleSubmit(row) {
      const allSentsDigitMatched = row.highTermSentVos?.every((item) =>
        this.sentDigitCheck(item),
      );
      if (!allSentsDigitMatched) return;

      const allFilled = row.highTermSentVos?.every((item) =>
        hasValidChar(item.editedText),
      );

      if (!allFilled) {
        this.$confirm(
          '<h4>确定要将此句译文保存为空吗?</h4><div>请谨慎操作！译文为空的场景一般应用于原文断句异常时，已将此句的译文合并入其他句子中。</div>',
          '译文为空预警',
          {
            dangerouslyUseHTMLString: true,
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning',
          },
        )
          .then(() => {
            this.doSubmit(row);
          })
          .catch(() => {
            row.highTermSentVos?.forEach((item) => {
              if (!hasValidChar(item.editedText)) {
                item.editedText = item.qcTxt && jsonTextToText(item.qcTxt);
                item.editedJson = item.qcTxt;
                item.errors = this.rebuildTermErrors(item.posVos);
                item.updateSign++;
              }
            });
          });
      } else {
        this.doSubmit(row);
      }
    },
    cacheExpandStatus() {
      this.records?.forEach((item) => {
        if (item.showAll) {
          // 没有唯一标识，只能如此：srcText + tgtText作为key
          this.expandStatus[item.srcText + item.tgtText] = item.showAll;
        } else {
          delete this.expandStatus[item.srcText + item.tgtText];
        }
      });
    },
    recoverExpandStatus() {
      this.records?.forEach((item) => {
        if (this.expandStatus[item.srcText + item.tgtText]) {
          item.showAll = this.expandStatus[item.srcText + item.tgtText];
          item.tarLimit = 'infinite';
        }
      });
    },
    doSubmit(row) {
      modifyHighTerm({
        orderId: this.queryParams.id,
        highTermSentVos: row.highTermSentVos.map((item) => ({
          id: item.id,
          highTermId: item.highTermId,
          sentId: item.sentId,
          srcTxt: item.srcTxt,
          qcTxt: item.editedJson,
        })),
      })
        .then(() => {
          this.$message({
            message: '提交成功',
            type: 'success',
          });
          this.fetchListData();
        })
        .catch((err) => {
          if (err.message === '该质控任务状态已修改') {
            this.$closeAndGoBack();
          }
        });
      this.cacheExpandStatus();
    },
    handleSelectionChange(val) {
      let selection = [];
      val.forEach(
        (item) => (selection = selection.concat(item.highTermSentVos ?? [])),
      );
      this.multiSelection = selection;
    },
    batchSubmit() {
      if (!this.multiSelection?.length) return;
      this.batchSubmitCheck()
        .then(() => {
          this.fullLoading = true;
          modifyHighTerm({
            orderId: this.queryParams.id,
            highTermSentVos: this.multiSelection.map((item) => ({
              id: item.id,
              highTermId: item.highTermId,
              sentId: item.sentId,
              srcTxt: item.srcTxt,
              qcTxt: item.editedJson,
            })),
          })
            .then(() => {
              this.$message({
                message: '提交成功',
                type: 'success',
              });
              this.fetchListData();
            })
            .catch((err) => {
              if (err.message === '该质控任务状态已修改') {
                this.$closeAndGoBack();
              }
            })
            .finally(() => {
              this.fullLoading = false;
            });
          this.cacheExpandStatus();
        })
        .catch(() => {});
    },
  },
};
</script>

<style lang="scss" scoped>
.messy-high-term {
  .fake-table-row {
    padding: 10px;
    &:not(:last-of-type) {
      border-bottom: 1px solid #ebeef5;
    }
  }

  .rich-text-column {
    .fake-table-row {
      padding: 0;
    }
  }
}

:deep(.el-table__body-wrapper) {
  .el-table_1_column_4,
  .el-table_1_column_5 {
    padding: 0;
    .cell {
      padding: 0;
    }
  }
}
</style>

<style lang="scss">
.hign-term-popover {
  .txt-replace-wrap {
    text-align: left;
    font-size: 12px;
    .highlight {
      color: red;
      white-space: nowrap;
    }
    .el-input {
      margin: 10px 0;
      > input {
        padding: 0 10px;
      }
    }
    .operate-btns {
      margin-top: 5px;
      text-align: right;
    }
  }
}
.highTermList {
  .li-border-b {
    border-bottom: 1px solid #ebeef5;
  }
}
</style>
